import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button, // Может понадобиться, если вернем внешнюю кнопку
    List,
    ListItem,
    alpha,
    ListItemText,
    Divider,
    useTheme,
    IconButton,
    InputAdornment,
    CircularProgress // Для индикации загрузки
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SendIcon from "@mui/icons-material/Send"; // Исправлен импорт
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useParams } from "react-router-dom";
import { Context } from "../index";
import { fetchInitialMessagesAPI, indexServers, sendRcon } from "../http/ServerAPI"; // Убедитесь, что fetchInitialMessagesAPI существует
import { observer } from "mobx-react-lite";

const getPlayersLabel = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return `${count} игрок`;
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return `${count} игрока`;
    return `${count} игроков`;
};

const ChatPage = observer(() => {
    const theme = useTheme();
    const { server_id } = useParams();
    const { user } = useContext(Context);
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [playerNames, setPlayerNames] = useState([]);
    const [message, setMessage] = useState("");
    const [playerCount, setPlayerCount] = useState(0);
    const [serverName, setServerName] = useState(server_id);
    // const [showParticipants, setShowParticipants] = useState(false); // Не используется
    // const [messageLength, setMessageLength] = useState(0); // Удалено
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true); // Состояние загрузки
    const chatEndRef = useRef(null);

    const indexServer = async () => {
        try {
            const data = await indexServers();
            user.setServers(data);
        } catch (e) {
            console.error("Ошибка получения данных сервера:", e);
        }
    };

    // Загрузка начальных сообщений через API
    const loadInitialMessages = async () => {
        if (!server_id) return; // Защита от undefined server_id
        setInitialLoading(true);
        try {
            // !!! Убедитесь, что fetchInitialMessagesAPI существует и возвращает правильный формат
            const initialData = await fetchInitialMessagesAPI(server_id);
            // Предполагаем, что initialData.messages - это массив сообщений
            setChatMessages(initialData.messages || []);
        } catch (err) {
            console.error("Ошибка загрузки начальных сообщений:", err);
            setChatMessages([]); // На случай ошибки
        } finally {
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        indexServer();
        const intervalId = setInterval(indexServer, 60000);
        return () => clearInterval(intervalId);
    }, [user]);

    useEffect(() => {
        if (user.userName) {
            setName(user.userName);
            setRole(user.role);
        }
    }, [user.userName, user.role]);

    useEffect(() => {
        if (user.servers.length && server_id) { // Проверяем server_id
            const server = user.servers?.find(item => item.server_id === parseInt(server_id, 10)); // parseInt с основанием
            setServerName(server ? server.title + " [" + getPlayersLabel(server.online) + "]" : server_id);
            setPlayerNames(server?.player_names ?? []);
            setPlayerCount(server?.online ?? 0)
        }
    }, [user.servers, server_id]); // Зависимость от server_id

    // Эффект для загрузки начальных сообщений при смене сервера
    useEffect(() => {
        loadInitialMessages();
        // Сбросим сообщения и состояние загрузки при смене сервера
        return () => {
            setChatMessages([]);
            setInitialLoading(true);
        };
    }, [server_id]); // Зависит только от server_id

    // Эффект для обработки новых сообщений из WebSocket (через user.chat)
    useEffect(() => {
        // Защита от undefined server_id
        if (!server_id) return;

        const currentServerId = parseInt(server_id, 10);
        // Проверяем, есть ли данные для текущего чата в user.chat
        const chatData = user.getStorageByChatId(currentServerId);

        if (chatData && Array.isArray(chatData.data)) {
            const storeMessages = chatData.data;
            const localLength = chatMessages.length;
            const storeLength = storeMessages.length;

            // Если в сторе больше сообщений, чем у нас локально, это значит пришли новые
            if (storeLength > localLength) {
                // Берем только новые сообщения
                const newMessages = storeMessages.slice(localLength);
                console.log(`Получено ${newMessages.length} новых сообщений для чата ${currentServerId}`);
                setChatMessages(prevMessages => [...prevMessages, ...newMessages]);
                // Прокрутка будет выполнена в useEffect, отслеживающем chatMessages
            }
            // Если localLength > storeLength, возможно, стор был сброшен.
            // Можно перезагрузить локальные сообщения или игнорировать.
        }
        // Если chatData пуст или data не массив, ничего не делаем или сбрасываем локально
        // else if (chatMessages.length > 0) {
        //     setChatMessages([]);
        // }
    }, [user.chat, server_id]); // Зависит от user.chat и server_id

    // Эффект для прокрутки вниз при изменении сообщений или завершении загрузки
    useEffect(() => {
        const scrollToBottom = () => {
            if (chatEndRef.current) {
                const executeScroll = () => {
                    if (chatEndRef.current) {
                        chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
                        // block: "end" может помочь, если родительский контейнер имеет padding
                    }
                };
                requestAnimationFrame(executeScroll);
            }
        };

        if (!initialLoading && chatMessages.length > 0) {
            scrollToBottom();
        }
        else if (initialLoading === false && chatMessages.length > 0) {
            scrollToBottom();
        }

    }, [chatMessages, initialLoading]);

    const sendMessage = async (sid, msg) => { // Используем параметры sid, msg
        try {
            // Проверка server_id перед отправкой
            if (!sid) {
                throw new Error("ID сервера не определен");
            }
            const data = await sendRcon(sid, msg, 'chat');
            console.log("Сообщение отправлено:", data);
            return data;
        } catch (e) {
            console.error("Ошибка отправки сообщения:", e);
            // Более точное получение сообщения об ошибке
            const errorMessage = e.response?.data?.message || e.message || "Неизвестная ошибка";
            throw new Error(errorMessage);
        }
    }

    const handleSendMessage = async () => { // Сделаем async
        const trimmedMessage = message.trim();
        // Проверка server_id и сообщения
        if (trimmedMessage && server_id) {
            try {
                // Отправляем сообщение на сервер
                await sendMessage(server_id, trimmedMessage);
                console.log("Сообщение успешно отправлено на сервер");

                // Создаем объект нового сообщения для локального отображения
                // Предполагаем, что сервер добавит имя отправителя
                // ВАЖНО: Убедитесь, что формат соответствует формату сообщений из API/WebSocket
                const newLocalMessage = {
                    date: new Date().toISOString(),
                    sender: name, // Добавляем sender для корректного отображения
                    chat_message: trimmedMessage, // Только текст сообщения от пользователя
                };

                // Добавляем сообщение в локальное состояние сразу для мгновенного отображения
                setChatMessages(prevMessages => [...prevMessages, newLocalMessage]);

                // Очищаем поле ввода
                setMessage("");

                // !!! КЛЮЧЕВОЙ МОМЕНТ: НЕ вызываем user.setChat здесь.
                // Сервер должен разослать это сообщение всем клиентам (включая отправителя),
                // и оно придет через WebSocket, обработается и добавится в user.chat.
                // Это обеспечит единообразие и избежит дублирования.

            } catch (error) {
                // Обработка ошибки отправки (например, показать уведомление пользователю)
                console.error("Не удалось отправить сообщение:", error.message);
                alert("Ошибка отправки сообщения: " + error.message); // Простое уведомление
            }
        } else if (!server_id) {
            console.warn("Попытка отправить сообщение без ID сервера");
            alert("Ошибка: ID сервера не определен.");
        }
    };

    const toggleSidebar = (open) => () => {
        setIsSidebarOpen(open);
    };

    // Функция для рендеринга сообщения с учетом отправителя
    const renderMessage = (msg, index) => {
        // Форматируем дату и время. toLocaleString с опциями дает гибкость.
        // Пример: "01.01.2024, 10:30"
        const messageDateTime = new Date(msg.date).toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });

        return (
            <Box
                key={index} // Лучше использовать уникальный ID сообщения, если он есть в данных
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start', // Все сообщения выравниваются слева
                    mb: 1,
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        maxWidth: '70%',
                        p: 1.5,
                        bgcolor: theme.palette.background.paper, // Единый цвет фона
                        borderRadius: '12px 12px 12px 0', // Единый стиль скругления
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                    }}
                >
                    {/* Отображаем дату и время */}
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'left', mb: 0.5, color: 'text.secondary' }}>
                        {messageDateTime}
                    </Typography>
                    {/* Отображаем само сообщение, предполагается, что имя игрока уже включено в chat_message */}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.chat_message}
                    </Typography>
                </Paper>
            </Box>
        );
    };


    return (
        <Box sx={{ display: "flex", height: "calc(100vh - 70px)", overflow: "hidden" }}>
            {/* Chat */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    bgcolor: "background.default",
                }}
            >
                {/* Mobile Sidebar Toggle Button */}
                <Box sx={{ display: { xs: "flex", md: "none" }, p: 1, bgcolor: "background.paper", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {serverName}
                    </Typography>
                    <IconButton onClick={toggleSidebar(true)} aria-label="Открыть меню">
                        <MenuIcon />
                    </IconButton>
                </Box>

                {/* Messages */}
                <Box sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.text.primary, 0.3),
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, 0.5),
                        }
                    },
                }}>
                    {initialLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : chatMessages.length > 0 ? (
                        // Используем улучшенную функцию рендеринга
                        chatMessages.map(renderMessage)
                    ) : (
                        <Paper
                            sx={{
                                textAlign: 'center',
                                py: 5,
                                bgcolor: 'background.paper',
                                color: 'text.secondary'
                            }}
                        >
                            <Typography variant="body1">Сообщений пока нет</Typography>
                            <Typography variant="body2">Будьте первым, кто напишет!</Typography>
                        </Paper>
                    )}
                    <div ref={chatEndRef} />
                </Box>

                {/* Input Form */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: 1,
                        borderColor: "divider",
                        p: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="outlined"
                        size="small"
                        placeholder="Введите сообщение..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        disabled={initialLoading || !server_id} // Отключаем при загрузке или отсутствии server_id
                        sx={{
                            mr: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '24px',
                                '& fieldset': {
                                },
                                '&:hover fieldset': {
                                },
                                '&.Mui-focused fieldset': {
                                    boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
                                },
                                '&.Mui-disabled': {
                                    '& fieldset': {
                                        borderColor: 'divider',
                                    },
                                    backgroundColor: alpha(theme.palette.action.disabledBackground, 0.1),
                                },
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Отправить сообщение"
                                        onClick={handleSendMessage}
                                        disabled={!message.trim() || initialLoading || !server_id} // Блокируем при загрузке/отсутствии server_id
                                        sx={{ borderRadius: '50%', mr: 0.5 }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            {/* Desktop Sidebar */}
            <Box
                sx={{
                    display: { xs: "none", md: "flex" },
                    width: "250px",
                    bgcolor: "background.paper",
                    borderLeft: 1,
                    borderColor: "divider",
                    flexDirection: "column",
                }}
            >
                <Typography variant="subtitle1" sx={{ p: 2, borderBottom: 1, borderColor: "divider", fontWeight: 'medium' }}>
                    Сервер
                </Typography>
                <List sx={{ py: 0 }}>
                    <ListItem sx={{ py: 1.5 }}>
                        <ListItemText
                            primary={serverName}
                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        />
                    </ListItem>
                </List>
                <Divider />
                <Typography variant="subtitle1" sx={{ p: 2, borderBottom: 1, borderColor: "divider", fontWeight: 'medium' }}>
                    Участники ({playerCount})
                </Typography>
                <List sx={{ flexGrow: 1, overflowY: "auto", py: 0 }}>
                    {playerNames.length > 0 ? (
                        playerNames.map((playerName) => (
                            <ListItem key={playerName} sx={{ py: 0.75, '&:hover': { bgcolor: 'action.hover' } }}>
                                <ListItemText
                                    primary={playerName}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        ))
                    ) : (
                        <ListItem sx={{ py: 1.5 }}>
                            <ListItemText primary="Нет участников" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                        </ListItem>
                    )}
                </List>
            </Box>

            {/* Sidebar (Mobile View) */}
            <SwipeableDrawer
                anchor="right"
                open={isSidebarOpen}
                onClose={toggleSidebar(false)}
                onOpen={toggleSidebar(true)}
                sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}
            >
                <Box
                    sx={{
                        bgcolor: "background.paper",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography variant="subtitle1" sx={{ p: 2, borderBottom: 1, borderColor: "divider", fontWeight: 'medium' }}>
                        Сервер
                    </Typography>
                    <List sx={{ py: 0 }}>
                        <ListItem sx={{ py: 1.5 }}>
                            <ListItemText
                                primary={serverName}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                            />
                        </ListItem>
                    </List>
                    <Divider />
                    <Typography variant="subtitle1" sx={{ p: 2, borderBottom: 1, borderColor: "divider", fontWeight: 'medium' }}>
                        Участники ({playerCount})
                    </Typography>
                    <List sx={{ flexGrow: 1, overflowY: "auto", py: 0 }}>
                        {playerNames.length > 0 ? (
                            playerNames.map((playerName) => (
                                <ListItem key={playerName} sx={{ py: 0.75, '&:hover': { bgcolor: 'action.hover' } }}>
                                    <ListItemText
                                        primary={playerName}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <ListItem sx={{ py: 1.5 }}>
                                <ListItemText primary="Нет участников" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                            </ListItem>
                        )}
                    </List>
                </Box>
            </SwipeableDrawer>
        </Box>
    );
});

export default ChatPage;